import { format } from 'date-fns';
import { isNil } from 'lodash-es';

export enum LogLevel {
  info = 0,
  warn = 1,
  error = 2,
}

type Log = {
  level: LogLevel;
  message: unknown[];
};

export class GroupedLogger {
  private logLevel = LogLevel.error;
  private showTimestamps = false;
  private groups: Map<string, Log[]>;

  private getLocalTimestamp(): string {
    return `[${format(new Date(), 'HH:mm:ss.SSS')}]`;
  }

  constructor(logLevel: LogLevel, showTimestamps: boolean) {
    this.logLevel = logLevel;
    this.showTimestamps = showTimestamps;
    this.groups = new Map();
  }

  /**
   * Add a log message to a specific group.
   * @param groupId A unique identifier for the log group.
   * @param message The log message to store.
   */
  log(
    groupId: string,
    message: Log['message'],
    level: LogLevel = LogLevel.info
  ): void {
    if (!this.groups.has(groupId)) {
      this.groups.set(groupId, []);
    }

    this.groups.get(groupId)!.push({
      level,
      message: this.showTimestamps
        ? [this.getLocalTimestamp(), ...message]
        : message,
    });
  }

  /**
   * Add a warning message to a specific group.
   * @param groupId A unique identifier for the log group.
   * @param message The warning message to store.
   */
  warn(groupId: string, message: Log['message']): void {
    this.log(groupId, message, LogLevel.warn);
  }

  /**
   * Output all messages in the group to the console and remove the group.
   * @param groupId The identifier of the group to flush.
   */
  flush(groupId: string, collapsed: boolean): void {
    const logs = this.groups.get(groupId);
    if (isNil(logs)) return;
    if (logs.every((log) => log.level < this.logLevel)) return;

    if (collapsed) {
      console.groupCollapsed(groupId);
    } else {
      console.group(groupId);
    }

    logs.forEach((log) => {
      if (log.level < this.logLevel) return;
      console[levelToMethod[log.level]](...log.message);
    });

    console.groupEnd();

    this.groups.delete(groupId);
  }

  /**
   * Flush all existing groups.
   */
  flushAll(): void {
    for (const groupId of this.groups.keys()) {
      this.flush(groupId, true);
    }
  }

  /**
   * Clear all logs without printing.
   */
  clear(): void {
    this.groups.clear();
  }
}

const levelToMethod = {
  [LogLevel.info]: 'log' as const,
  [LogLevel.warn]: 'warn' as const,
  [LogLevel.error]: 'error' as const,
};
